import ReactECharts from 'echarts-for-react';
import { indexOf } from 'lodash';
import { useRecordContext, useTranslate } from 'react-admin';
import globalUseStyles from '../../../../styles/globalCustomStyles';
import ChartLoading from '../../../aegeon/charts/ChartLoading';
import ChartNoData from '../../../aegeon/charts/ChartNoData';
import { getColors, getInterval, getNote } from '../../../aegeon/compute_performances';
import { getDataMarkPoint, is_value_defined } from '../../../aegeon/helper';

const SiteEnergeticPerformanceDPEGraph = (props) => {
  const globalClasses = globalUseStyles();
  const t = useTranslate();

  const { type, data, isLoading, dpeTable } = props

  const record = useRecordContext();

  // TODO : for the DPE_MAX : display both on the same graph with a neutral color
  const perf_max = data?.performances?.find((p) => p.type === "DPE_ENEP");
  const perf = type !== "DPE_MAX" ? data?.performances?.find((p) => p.type === type) : perf_max;
  const value = is_value_defined(perf?.value) ? Math.round(perf?.value * 100) / 100 : null;
  const unit = perf?.unit;

  const isDetailedCard = props.isNotDetailed ? false : true

  const sizes = isDetailedCard ? {
    arrowLabelPosition: [3, 8],
    barWidth: 30,
    symbolSizeRect: [160, 30],
    symbolSizeRect2: [160, 40],
    fontSize: 14,
    fontSizeSubtitle: 12,
    labelPosition: [5, 10],
  } : {
    arrowLabelPosition: [-2, 2],
    barWidth: 18,
    symbolSizeRect: [160, 30],
    symbolSizeRect2: [160, 40],
    fontSize: 11,
    fontSizeSubtitle: 11,
    labelPosition: [5, 5]
  }

  const interval = getInterval(type, dpeTable);
  const note = getNote(value, dpeTable, type);

  const values = {
    "A": 20,
    "B": 30,
    "C": 40,
    "D": 50,
    "E": 60,
    "F": 70,
    "G": 80
  }

  const colors = getColors(type)

  let option = {
    tooltip: {
      position: [10, -50],
      formatter: (params) => {
        if (params.dataIndex === 6) {
          return `A : < ${interval[1]} ${unit}`
        } else if (params.dataIndex === 5) {
          return `B : entre ${interval[1]} et ${interval[2]} ${unit}`
        } else if (params.dataIndex === 4) {
          return `C : entre ${interval[2]} et ${interval[3]} ${unit}`
        } else if (params.dataIndex === 3) {
          return `D : entre ${interval[3]} et ${interval[4]} ${unit}`
        } else if (params.dataIndex === 2) {
          return `E : entre ${interval[4]} et ${interval[5]} ${unit}`
        } else if (params.dataIndex === 1) {
          return `F : entre ${interval[5]} et ${interval[6]} ${unit}`
        } else if (params.dataIndex === 0) {
          return `G : > ${interval[6]} ${unit}`
        }
      }
    },
    grid: {
      top: 0,
      bottom: 10,
    },
    yAxis: {
      show: false,
      min: -1,
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        show: true,
        fontSize: 14,
        color: "black",
      },
      data: Object.keys(values).reverse(),
    },
    color: Object.values(colors).reverse(),
    barWidth: sizes.barWidth,
    emphasis: {
      disabled: true
    },
    xAxis: {
      show: false,
      max: 150,
    },
    animation: false,
    series: [{
      name: "",
      type: "bar",
      markPoint: {
        symbol: "diamond",
        emphasis: {
          disabled: true
        },
        animation: false,
        symbolSize: sizes.barWidth,
        data: [
          getDataMarkPoint("A", values, colors, sizes.arrowLabelPosition, isDetailedCard),
          getDataMarkPoint("B", values, colors, sizes.arrowLabelPosition, isDetailedCard),
          getDataMarkPoint("C", values, colors, sizes.arrowLabelPosition, isDetailedCard),
          getDataMarkPoint("D", values, colors, sizes.arrowLabelPosition, isDetailedCard),
          getDataMarkPoint("E", values, colors, sizes.arrowLabelPosition, isDetailedCard),
          getDataMarkPoint("F", values, colors, sizes.arrowLabelPosition, isDetailedCard),
          getDataMarkPoint("G", values, colors, sizes.arrowLabelPosition, isDetailedCard),
          {
            symbol: "rect",
            symbolSize: sizes.symbolSizeRect,
            symbolOffset: [-15, note === "G" ? -30 : 0],
            value: `${note}`,
            label: {
              show: true,
              color: "black",
              fontWeight: "600",
              fontSize: sizes.fontSize,
              fontFamily: 'Montserrat, sans-serif',
            },
            itemStyle: {
              color: colors[note],
            },
            coord: [140, indexOf(Object.keys(values).reverse(), note)],
          },
          {
            symbol: "rect",
            symbolSize: sizes.symbolSizeRect2,
            symbolOffset: [-15, note === "G" ? 0 : 30],
            value: `${value} ${unit}`,
            label: {
              show: true,
              color: "black",
              fontFamily: 'Montserrat, sans-serif',
              fontSize: sizes.fontSizeSubtitle,
            },
            itemStyle: {
              color: colors[note],
            },
            coord: [140, indexOf(Object.keys(values).reverse(), note)],
          }
        ],
      },
      markLine: {
        symbol: "none",
        animation: false,
        lineStyle: {
          color: colors[note],
          type: "dashed",
          width: 1,
        },
        data: [
          [
            {
              coord: [values[note], indexOf(Object.keys(values).reverse(), note)],
              emphasis: {
                disabled: true
              }
            },
            {
              coord: [120, indexOf(Object.keys(values).reverse(), note)]
            }
          ],
        ],
      },
      label: {
        show: true,
        position: sizes.labelPosition,
        color: "black",
        fontWeight: "500",
        fontFamily: 'Montserrat, sans-serif',
        fontSize: 10,
        formatter: (params) => {
          if (params.dataIndex === 6) {
            return `< ${interval[1]}`
          } else if (params.dataIndex === 5) {
            return `${interval[1]} à ${interval[2]}`
          } else if (params.dataIndex === 4) {
            return `${interval[2]} à ${interval[3]}`
          } else if (params.dataIndex === 3) {
            return `${interval[3]} à ${interval[4]}`
          } else if (params.dataIndex === 2) {
            return `${interval[4]} à ${interval[5]}`
          } else if (params.dataIndex === 1) {
            return `${interval[5]} à ${interval[6]}`
          } else if (params.dataIndex === 0) {
            return `> ${interval[6]}`
          }
        }
      },
      data: Object.values(values).reverse(),
      colorBy: "data"
    }]
  }

  return (
    isLoading ?
      <ChartLoading /> :
      perf && value !== undefined && value !== null ?
        <div className={globalClasses.chartDPE}>
          <ReactECharts
            option={option}
            notMerge={true}
            style={{ height: '100%', width: '100%' }}
          />

        </div>
        :
        <ChartNoData />
  )
}

export default SiteEnergeticPerformanceDPEGraph